import React, { PropsWithChildren, useState, useEffect, useCallback, useMemo } from 'react';

type MessageConfig = { text: string; type: number | null };
type DialogConfig = { text: string; confirm: (data?: unknown) => void };
type GeolocationInput = { latitude?: string | number | null; longitude?: string | number | null } | null;

interface AppContextType {
    init: boolean;
    finishInit: () => void;
    hideMenu: boolean;
    toggleMenu: (force?: boolean) => void;
    geolocation: { latitude: string; longitude: string };
    updateGeolocation: (coords: GeolocationInput) => void;
    loading: string;
    newLoading: (text: string) => void;
    message: MessageConfig;
    newMessage: (config: MessageConfig) => void;
    dialog: DialogConfig;
    newDialog: (config: DialogConfig) => void;
    settings: { camera: string };
    updateSetting: (name: string, value: string) => void;
    location: string;
    updateLocation: (location: string) => void;
    isOnline: boolean;
}

const AppContext = React.createContext<AppContextType>({
    init: false,
    finishInit: () => {},
    hideMenu: true,
    toggleMenu: () => {},
    geolocation: { latitude: '', longitude: '' },
    updateGeolocation: () => {},
    loading: '',
    newLoading: () => {},
    message: { text: '', type: null },
    newMessage: () => {},
    dialog: { text: '', confirm: () => {} },
    newDialog: () => {},
    settings: { camera: '' },
    updateSetting: () => {},
    location: '',
    updateLocation: () => {},
    isOnline: true
});

type appProps = {};

export const AppContextProvider = (props: PropsWithChildren<appProps>) => {
    const [init, setInit] = useState(false);
    const [hideMenu, setHideMenu] = useState(true);
    const [geolocation, setGeolocation] = useState({ latitude: '', longitude: '' });
    const [loading, setLoading] = useState('');
    const [message, setMessage] = useState<{ text: string; type: number | null }>({ text: '', type: null });
    const [dialog, setDialog] = useState<DialogConfig>({ text: '', confirm: () => {} });
    const [settings, setSettings] = useState({ camera: '' });
    const [location, setLocation] = useState('');
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    const finishInit = useCallback(() => {
        setInit(true);
    }, []);

    const toggleMenu = useCallback((forceState?: boolean) => {
        if (typeof forceState === 'boolean') {
            setHideMenu(!forceState);
        } else {
            setHideMenu(prevHideMenu => !prevHideMenu);
        }
    }, []);

    const newLoading = useCallback((text: string) => {
        setLoading(text);
    }, []);

    const newMessage = useCallback((config: MessageConfig) => {
        setMessage(config);
    }, []);

    const newDialog = useCallback((config: DialogConfig) => {
        setDialog(config);
    }, []);

    const updateGeolocation = useCallback((coords: GeolocationInput) => {
        const latitude = String(coords?.latitude ?? '');
        const longitude = String(coords?.longitude ?? '');
        setGeolocation({ latitude, longitude });
    }, []);

    const updateSetting = useCallback((name: string, value: string) => {
        setSettings((prevSettings) => {
            const newSettings = { ...prevSettings, [name]: value };
            localStorage.setItem('deviceSettings', JSON.stringify(newSettings));
            return newSettings;
        });
    }, []);

    const updateLocation = useCallback((locationString: string) => {
        setLocation(locationString);
    }, []);

    // Network status monitoring
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            console.log('[Network] Online');
        };
        
        const handleOffline = () => {
            setIsOnline(false);
            console.log('[Network] Offline');
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Load saved settings on mount
    useEffect(() => {
        const settingsTemplate: { camera: string } = { camera: '' };
        const raw = localStorage.getItem('deviceSettings');
        let savedSettings: { camera: string };

        if (raw) {
            const parsed: Partial<{ camera: string }> = JSON.parse(raw);
            savedSettings = {
                ...settingsTemplate,
                ...Object.fromEntries(
                    Object.keys(settingsTemplate).map((k) => [k, parsed[k as keyof typeof settingsTemplate] || settingsTemplate[k as keyof typeof settingsTemplate]])
                ),
            } as { camera: string };
        } else {
            savedSettings = settingsTemplate;
        }

        setSettings(savedSettings);
        localStorage.setItem('deviceSettings', JSON.stringify(savedSettings));
    }, []);

    const providerConfig: AppContextType = useMemo(() => ({
        init,
        finishInit,
        hideMenu,
        toggleMenu,
        geolocation,
        updateGeolocation,
        loading,
        newLoading,
        message,
        newMessage,
        dialog,
        newDialog,
        settings,
        updateSetting,
        location,
        updateLocation,
        isOnline
    }), [
        init,
        finishInit,
        hideMenu,
        toggleMenu,
        geolocation,
        updateGeolocation,
        loading,
        newLoading,
        message,
        newMessage,
        dialog,
        newDialog,
        settings,
        updateSetting,
        location,
        updateLocation,
        isOnline
    ]);

    return (
        <AppContext.Provider value={providerConfig}>
            {props.children}
        </AppContext.Provider>
    );
};

export default AppContext;
